import { useState, useEffect, useRef } from 'react'
import { Box, Button, Typography, Modal, Icon } from 'components/atomic'
import { getAxiosInstance } from 'services/api'
import { toast } from 'react-toastify'
import { useMobile } from 'hooks'
import { track } from 'utils/analytics'

const api = getAxiosInstance()

interface SettingsModalProps {
  isOpen: boolean
  onClose: () => void
}

type SettingsTab = 'notifications' | 'account' | 'privacy' | 'connections'

interface SteamConnection {
  connected: boolean
  steam_id: string | null
  steam_username: string | null
  steam_avatar_url: string | null
  connected_at: string | null
  can_disconnect: boolean
  days_until_can_change: number
}

interface DiscordConnection {
  connected: boolean
  discord_id: string | null
  discord_username: string | null
  connected_at: string | null
}

interface NotificationPreferences {
  dailyDigest: {
    enabled: boolean
    time: string
    includeComments: boolean
    includeLikes: boolean
    includeTeamRequests: boolean
    includeMentions: boolean
    includeUnreadNotifications: boolean
  }
  weeklySummary: {
    enabled: boolean
    dayOfWeek: string
  }
  realTimeNotifications: {
    enabled: boolean
  }
}

const SettingsModal = ({ isOpen, onClose }: SettingsModalProps) => {
  const isMobile = useMobile()
  const [activeTab, setActiveTab] = useState<SettingsTab>('notifications')
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['notifications']))
  const [loading, setLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    dailyDigest: {
      enabled: true,
      time: '08:00',
      includeComments: true,
      includeLikes: true,
      includeTeamRequests: true,
      includeMentions: true,
      includeUnreadNotifications: true,
    },
    weeklySummary: {
      enabled: false,
      dayOfWeek: 'Sunday',
    },
    realTimeNotifications: {
      enabled: true,
    },
  })

  // Steam connection state
  const [steamConnection, setSteamConnection] = useState<SteamConnection>({
    connected: false,
    steam_id: null,
    steam_username: null,
    steam_avatar_url: null,
    connected_at: null,
    can_disconnect: true,
    days_until_can_change: 0,
  })
  const [steamLoading, setSteamLoading] = useState(false)
  const [steamConnecting, setSteamConnecting] = useState(false)

  // Discord connection state
  const [discordConnection, setDiscordConnection] = useState<DiscordConnection>({
    connected: false,
    discord_id: null,
    discord_username: null,
    connected_at: null,
  })
  const [discordLoading, setDiscordLoading] = useState(false)
  const [discordConnecting, setDiscordConnecting] = useState(false)

  // Refs for Discord polling cleanup (prevent memory leaks on unmount)
  const discordPollRef = useRef<number | null>(null)
  const discordTimeoutRef = useRef<number | null>(null)
  const discordCloseCheckRef = useRef<number | null>(null)

  // Cleanup Discord polling on unmount
  useEffect(() => {
    return () => {
      if (discordPollRef.current) clearInterval(discordPollRef.current)
      if (discordTimeoutRef.current) clearTimeout(discordTimeoutRef.current)
      if (discordCloseCheckRef.current) clearInterval(discordCloseCheckRef.current)
    }
  }, [])

  // Fetch current preferences
  useEffect(() => {
    if (isOpen) {
      fetchPreferences()
    }
  }, [isOpen])

  const fetchPreferences = async () => {
    setLoading(true)
    try {
      const response = await api.get('/settings/notification-preferences')
      if (response.data.success) {
        setPreferences(response.data.preferences)
      }
    } catch (error: any) {
      console.error('Error fetching preferences:', error)
      toast.error('Failed to load settings')
    } finally {
      setLoading(false)
    }
  }

  // Fetch connection statuses when Connections tab is active
  useEffect(() => {
    if (isOpen && activeTab === 'connections') {
      fetchSteamStatus()
      fetchDiscordStatus()
    }
  }, [isOpen, activeTab])

  const fetchSteamStatus = async () => {
    setSteamLoading(true)
    try {
      const response = await api.get('/auth/steam/status')
      if (response.data.success) {
        setSteamConnection({
          connected: response.data.connected,
          steam_id: response.data.steam_id,
          steam_username: response.data.steam_username,
          steam_avatar_url: response.data.steam_avatar_url,
          connected_at: response.data.connected_at,
          can_disconnect: response.data.can_disconnect,
          days_until_can_change: response.data.days_until_can_change,
        })
      }
    } catch (error: any) {
      console.error('Error fetching Steam status:', error)
    } finally {
      setSteamLoading(false)
    }
  }

  const handleSteamConnect = async () => {
    setSteamConnecting(true)
    try {
      const response = await api.get('/auth/steam/connect')
      if (response.data.success && response.data.authUrl) {
        track('settings_steam_connected')
        // Redirect to Steam for authentication
        window.location.href = response.data.authUrl
      }
    } catch (error: any) {
      console.error('Error initiating Steam connection:', error)
      toast.error('Failed to connect to Steam')
      setSteamConnecting(false)
    }
  }

  const handleSteamDisconnect = async () => {
    if (!steamConnection.can_disconnect) {
      toast.error(`You can change your Steam account in ${steamConnection.days_until_can_change} days`)
      return
    }

    try {
      const response = await api.post('/auth/steam/disconnect')
      if (response.data.success) {
        setSteamConnection({
          connected: false,
          steam_id: null,
          steam_username: null,
          steam_avatar_url: null,
          connected_at: null,
          can_disconnect: true,
          days_until_can_change: 30,
        })
        toast.success('Steam account disconnected')
      }
    } catch (error: any) {
      console.error('Error disconnecting Steam:', error)
      toast.error(error.response?.data?.error || 'Failed to disconnect Steam')
    }
  }

  const fetchDiscordStatus = async () => {
    setDiscordLoading(true)
    try {
      const response = await api.get('/auth/discord/status')
      if (response.data.success) {
        setDiscordConnection({
          connected: response.data.connected,
          discord_id: response.data.discord_id,
          discord_username: response.data.discord_username,
          connected_at: response.data.discord_connected_at,
        })
      }
    } catch (error: any) {
      console.error('Error fetching Discord status:', error)
    } finally {
      setDiscordLoading(false)
    }
  }

  const cleanupDiscordPolling = () => {
    if (discordPollRef.current) { clearInterval(discordPollRef.current); discordPollRef.current = null }
    if (discordTimeoutRef.current) { clearTimeout(discordTimeoutRef.current); discordTimeoutRef.current = null }
    if (discordCloseCheckRef.current) { clearInterval(discordCloseCheckRef.current); discordCloseCheckRef.current = null }
  }

  const handleDiscordConnect = async () => {
    if (discordConnecting) return
    setDiscordConnecting(true)
    try {
      const response = await api.get('/auth/discord/connect')
      if (response.data.success && response.data.authUrl) {
        const popup = window.open(response.data.authUrl, 'discord-auth', 'width=500,height=700')

        if (!popup || popup.closed) {
          toast.error('Popup was blocked. Please allow popups for this site and try again.')
          setDiscordConnecting(false)
          return
        }

        // Poll for connection status every 2s
        discordPollRef.current = window.setInterval(async () => {
          try {
            const statusRes = await api.get('/auth/discord/status')
            if (statusRes.data.success && statusRes.data.connected) {
              cleanupDiscordPolling()
              setDiscordConnection({
                connected: true,
                discord_id: statusRes.data.discord_id,
                discord_username: statusRes.data.discord_username,
                connected_at: statusRes.data.discord_connected_at,
              })
              setDiscordConnecting(false)
              toast.success('Discord account connected!')
              track('settings_discord_connected')
              if (popup && !popup.closed) popup.close()
            }
          } catch { /* ignore polling errors */ }
        }, 2000)

        // Timeout: stop polling after 5 minutes
        discordTimeoutRef.current = window.setTimeout(() => {
          cleanupDiscordPolling()
          setDiscordConnecting(false)
        }, 5 * 60 * 1000)

        // Detect popup close → do one final status check
        discordCloseCheckRef.current = window.setInterval(() => {
          if (popup.closed) {
            if (discordCloseCheckRef.current) {
              clearInterval(discordCloseCheckRef.current)
              discordCloseCheckRef.current = null
            }
            setTimeout(async () => {
              try {
                const statusRes = await api.get('/auth/discord/status')
                if (statusRes.data.success && statusRes.data.connected) {
                  setDiscordConnection({
                    connected: true,
                    discord_id: statusRes.data.discord_id,
                    discord_username: statusRes.data.discord_username,
                    connected_at: statusRes.data.discord_connected_at,
                  })
                  toast.success('Discord account connected!')
                  track('settings_discord_connected')
                }
              } catch { /* ignore */ }
              cleanupDiscordPolling()
              setDiscordConnecting(false)
            }, 1000)
          }
        }, 500)
      }
    } catch (error: any) {
      console.error('Error initiating Discord connection:', error)
      toast.error(error.response?.data?.error || 'Failed to connect to Discord')
      setDiscordConnecting(false)
    }
  }

  const handleDiscordDisconnect = async () => {
    try {
      const response = await api.post('/auth/discord/disconnect')
      if (response.data.success) {
        setDiscordConnection({
          connected: false,
          discord_id: null,
          discord_username: null,
          connected_at: null,
        })
        toast.success('Discord account disconnected')
      }
    } catch (error: any) {
      console.error('Error disconnecting Discord:', error)
      toast.error(error.response?.data?.error || 'Failed to disconnect Discord')
    }
  }

  const handleSave = async () => {
    setSaving(true)
    try {
      const response = await api.put('/settings/notification-preferences', preferences)
      if (response.data.success) {
        toast.success('Settings saved successfully!')
        track('settings_preferences_saved')
        onClose()
      }
    } catch (error: any) {
      console.error('Error saving preferences:', error)
      toast.error('Failed to save settings')
    } finally {
      setSaving(false)
    }
  }

  const handleReset = async () => {
    try {
      const response = await api.post('/settings/notification-preferences/reset')
      if (response.data.success) {
        setPreferences(response.data.preferences)
        toast.success('Settings reset to defaults!')
      }
    } catch (error: any) {
      console.error('Error resetting preferences:', error)
      toast.error('Failed to reset settings')
    }
  }

  const toggleDailyDigest = () => {
    setPreferences({
      ...preferences,
      dailyDigest: {
        ...preferences.dailyDigest,
        enabled: !preferences.dailyDigest.enabled,
      },
    })
  }

  const updateDailyDigestTime = (time: string) => {
    setPreferences({
      ...preferences,
      dailyDigest: {
        ...preferences.dailyDigest,
        time,
      },
    })
  }

  const toggleDailyDigestOption = (option: keyof Omit<NotificationPreferences['dailyDigest'], 'enabled' | 'time'>) => {
    setPreferences({
      ...preferences,
      dailyDigest: {
        ...preferences.dailyDigest,
        [option]: !preferences.dailyDigest[option],
      },
    })
  }

  const toggleWeeklySummary = () => {
    setPreferences({
      ...preferences,
      weeklySummary: {
        ...preferences.weeklySummary,
        enabled: !preferences.weeklySummary.enabled,
      },
    })
  }

  const toggleRealTime = () => {
    setPreferences({
      ...preferences,
      realTimeNotifications: {
        enabled: !preferences.realTimeNotifications.enabled,
      },
    })
  }

  const tabItems: { id: SettingsTab; label: string; icon: string }[] = [
    { id: 'notifications', label: 'Notifications', icon: 'bell' },
    { id: 'account', label: 'Account', icon: 'account' },
    { id: 'privacy', label: 'Privacy', icon: 'lock' },
    { id: 'connections', label: 'Connections', icon: 'link' },
  ]

  const toggleSection = (section: string) => {
    setExpandedSections(prev => {
      const next = new Set(prev)
      if (next.has(section)) {
        next.delete(section)
      } else {
        next.add(section)
        // Fetch connection statuses when connections section is expanded on mobile
        if (section === 'connections') {
          fetchSteamStatus()
          fetchDiscordStatus()
        }
      }
      return next
    })
  }

  // Section header component for mobile accordion - premium card design
  const MobileSectionHeader = ({ id, title, icon, description }: { id: string; title: string; icon: string; description?: string }) => {
    const isExpanded = expandedSections.has(id)
    return (
      <button
        onClick={() => toggleSection(id)}
        className={`w-full flex items-center justify-between p-4 rounded-2xl mb-3 transition-all duration-200 border ${
          isExpanded
            ? 'border-green/30 shadow-[0_0_16px_rgba(56,239,125,0.12)] backdrop-blur bg-green/[0.06]'
            : 'bg-dark-card/80 backdrop-blur border-dark-border hover:border-green/20 hover:shadow-[0_0_12px_rgba(56,239,125,0.06)]'
        }`}
      >
        <Box alignCenter className="gap-3">
          <Box
            className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all duration-200 ${
              isExpanded
                ? 'bg-gradient-to-br from-green/20 to-cyan/15 text-green'
                : 'bg-dark-border/60 text-gray-400'
            }`}
          >
            <Icon name={icon as any} size={20} />
          </Box>
          <Box col className="text-left">
            <Typography
              variant="h6"
              className={`text-base font-semibold ${isExpanded ? 'text-green' : 'text-white'}`}
            >
              {title}
            </Typography>
            {description && (
              <Typography color="secondary" className="text-xs">
                {description}
              </Typography>
            )}
          </Box>
        </Box>
        <Box
          className={`w-8 h-8 rounded-full flex items-center justify-center transition-all duration-200 ${
            isExpanded ? 'bg-green/15 rotate-180' : 'bg-dark-border/60'
          }`}
        >
          <Icon
            name="arrowDownLine"
            size={20}
            className={isExpanded ? 'text-green' : 'text-gray-500'}
          />
        </Box>
      </button>
    )
  }

  // Reusable toggle switch component
  const ToggleSwitch = ({ checked, onChange }: { checked: boolean; onChange: () => void }) => (
    <label className="relative inline-flex items-center cursor-pointer flex-shrink-0">
      <input
        type="checkbox"
        className="sr-only peer"
        checked={checked}
        onChange={onChange}
      />
      <div className={`w-11 h-6 bg-gray-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-green ${checked ? 'shadow-[0_0_8px_rgba(56,239,125,0.3)]' : ''}`}></div>
    </label>
  )

  // Reusable content sections (used in both mobile accordion and desktop tabs)
  const NotificationsContent = () => (
    <>
      {loading ? (
        <Box className="py-12 flex-col items-center" center>
          <Icon name="loader" spin size={24} color="primaryBtn" />
          <Typography color="secondary" className="mt-3 text-sm">Loading settings...</Typography>
        </Box>
      ) : (
        <>
          {/* Daily Digest Section */}
          <Box className="rounded-xl bg-white/[0.02] border border-dark-border p-4 sm:p-5 mb-4" col>
            <Box alignCenter justify="between" className="mb-4 flex-wrap gap-2">
              <Box alignCenter className="gap-3 flex-1 min-w-0">
                <Box className="w-9 h-9 rounded-xl bg-gradient-to-br from-green/15 to-cyan/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="bell" size={18} color="green" />
                </Box>
                <Box col className="min-w-0">
                  <Typography variant="h5" className="text-base sm:text-lg">Daily Digest</Typography>
                  <Typography color="secondary" className="text-xs sm:text-sm">
                    Get a summary of your social interactions once per day
                  </Typography>
                </Box>
              </Box>
              <ToggleSwitch checked={preferences.dailyDigest.enabled} onChange={toggleDailyDigest} />
            </Box>

            {preferences.dailyDigest.enabled && (
              <Box col className="space-y-4 ml-0 sm:ml-12">
                {/* Time Selection */}
                <Box col>
                  <Typography className="mb-2 text-sm">Delivery Time</Typography>
                  <select
                    value={preferences.dailyDigest.time}
                    onChange={(e) => updateDailyDigestTime(e.target.value)}
                    className="bg-dark-gray text-white px-4 py-2.5 rounded-xl border border-dark-border focus:outline-none focus:ring-1 focus:ring-green/50 w-full sm:w-auto appearance-none cursor-pointer"
                    style={{ colorScheme: 'dark' }}
                  >
                    <option value="08:00" className="bg-dark-gray text-white">8:00 AM (Morning)</option>
                    <option value="12:00" className="bg-dark-gray text-white">12:00 PM (Noon)</option>
                    <option value="18:00" className="bg-dark-gray text-white">6:00 PM (Evening)</option>
                    <option value="20:00" className="bg-dark-gray text-white">8:00 PM (Night)</option>
                  </select>
                </Box>

                {/* Include Options */}
                <Box col className="space-y-3">
                  <Typography className="text-sm font-semibold">Include in Digest:</Typography>

                  <label className="flex items-center space-x-3 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={preferences.dailyDigest.includeComments}
                      onChange={() => toggleDailyDigestOption('includeComments')}
                      className="w-5 h-5 bg-dark-gray/70 border-dark-border rounded-md accent-green focus:ring-green focus:ring-1"
                    />
                    <Typography className="text-sm">Comments on your posts</Typography>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={preferences.dailyDigest.includeLikes}
                      onChange={() => toggleDailyDigestOption('includeLikes')}
                      className="w-5 h-5 bg-dark-gray/70 border-dark-border rounded-md accent-green focus:ring-green focus:ring-1"
                    />
                    <Typography className="text-sm">Likes and reactions</Typography>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={preferences.dailyDigest.includeTeamRequests}
                      onChange={() => toggleDailyDigestOption('includeTeamRequests')}
                      className="w-5 h-5 bg-dark-gray/70 border-dark-border rounded-md accent-green focus:ring-green focus:ring-1"
                    />
                    <Typography className="text-sm">Team requests</Typography>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={preferences.dailyDigest.includeMentions}
                      onChange={() => toggleDailyDigestOption('includeMentions')}
                      className="w-5 h-5 bg-dark-gray/70 border-dark-border rounded-md accent-green focus:ring-green focus:ring-1"
                    />
                    <Typography className="text-sm">Mentions</Typography>
                  </label>

                  <label className="flex items-center space-x-3 cursor-pointer min-h-[44px]">
                    <input
                      type="checkbox"
                      checked={preferences.dailyDigest.includeUnreadNotifications}
                      onChange={() => toggleDailyDigestOption('includeUnreadNotifications')}
                      className="w-5 h-5 bg-dark-gray/70 border-dark-border rounded-md accent-green focus:ring-green focus:ring-1"
                    />
                    <Typography className="text-sm">Unread notifications</Typography>
                  </label>
                </Box>
              </Box>
            )}
          </Box>

          {/* Weekly Summary Section */}
          <Box className="rounded-xl bg-white/[0.02] border border-dark-border p-4 sm:p-5 mb-4" col>
            <Box alignCenter justify="between" className="flex-wrap gap-2">
              <Box alignCenter className="gap-3 flex-1 min-w-0">
                <Box className="w-9 h-9 rounded-xl bg-gradient-to-br from-green/15 to-cyan/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="calendar" size={18} color="green" />
                </Box>
                <Box col className="min-w-0">
                  <Typography variant="h5" className="text-base sm:text-lg">Weekly Summary</Typography>
                  <Typography color="secondary" className="text-xs sm:text-sm">
                    Get a weekly overview of your activity and engagement
                  </Typography>
                </Box>
              </Box>
              <ToggleSwitch checked={preferences.weeklySummary.enabled} onChange={toggleWeeklySummary} />
            </Box>
          </Box>

          {/* Real-Time Notifications Section */}
          <Box className="rounded-xl bg-white/[0.02] border border-dark-border p-4 sm:p-5 mb-4" col>
            <Box alignCenter justify="between" className="flex-wrap gap-2">
              <Box alignCenter className="gap-3 flex-1 min-w-0">
                <Box className="w-9 h-9 rounded-xl bg-gradient-to-br from-green/15 to-cyan/10 flex items-center justify-center flex-shrink-0">
                  <Icon name="wifi" size={18} color="green" />
                </Box>
                <Box col className="min-w-0">
                  <Typography variant="h5" className="text-base sm:text-lg">Real-Time Notifications</Typography>
                  <Typography color="secondary" className="text-xs sm:text-sm">
                    Receive instant notifications for all interactions
                  </Typography>
                </Box>
              </Box>
              <ToggleSwitch checked={preferences.realTimeNotifications.enabled} onChange={toggleRealTime} />
            </Box>
          </Box>
        </>
      )}
    </>
  )

  const AccountContent = () => (
    <Box col className="py-8 sm:py-12 items-center text-center">
      <Box className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green/15 to-cyan/10 flex items-center justify-center mb-4">
        <Icon name="account" size={28} color="green" />
      </Box>
      <Typography variant="h5" className="text-lg mb-2">Account Settings</Typography>
      <Typography color="secondary" className="text-sm mb-4 max-w-xs">
        Manage your profile, display name, and account preferences all in one place.
      </Typography>
      <span className="inline-flex items-center px-3 py-1 bg-green/5 border border-green/10 rounded-full text-green/60 text-xs font-medium">
        Coming Soon
      </span>
    </Box>
  )

  const PrivacyContent = () => (
    <Box col className="py-8 sm:py-12 items-center text-center">
      <Box className="w-16 h-16 rounded-2xl bg-gradient-to-br from-green/15 to-cyan/10 flex items-center justify-center mb-4">
        <Icon name="lock" size={28} color="green" />
      </Box>
      <Typography variant="h5" className="text-lg mb-2">Privacy Settings</Typography>
      <Typography color="secondary" className="text-sm mb-4 max-w-xs">
        Control your data visibility, profile privacy, and notification sharing preferences.
      </Typography>
      <span className="inline-flex items-center px-3 py-1 bg-green/5 border border-green/10 rounded-full text-green/60 text-xs font-medium">
        Coming Soon
      </span>
    </Box>
  )

  const ConnectionsContent = () => (
    <Box col>
      {(steamLoading || discordLoading) ? (
        <Box className="py-12 flex-col items-center" center>
          <Icon name="loader" spin size={24} color="primaryBtn" />
          <Typography color="secondary" className="mt-3 text-sm">Loading connections...</Typography>
        </Box>
      ) : (
        <>
          {/* Steam Connection Section */}
          <Box className="border border-dark-border rounded-2xl bg-white/[0.02] p-4 sm:p-6 mb-4 sm:mb-6 transition-all duration-200 hover:border-green/20 hover:shadow-[0_0_24px_rgba(56,239,125,0.06)]" col>
            <Box alignCenter justify="between" className="mb-4 flex-wrap gap-3">
              <Box alignCenter className="gap-3 sm:gap-4">
                {/* Steam Logo */}
                <img
                  src="/assets/steam.jpg"
                  alt="Steam"
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0 object-cover ring-2 ${
                    steamConnection.connected
                      ? 'ring-green/30 shadow-[0_0_12px_rgba(56,239,125,0.15)]'
                      : 'ring-dark-border'
                  }`}
                />
                <Box col className="min-w-0">
                  <Typography variant="h5" className="text-base sm:text-lg">Steam</Typography>
                  {steamConnection.connected ? (
                    <Typography color="secondary" className="text-xs sm:text-sm truncate">
                      Connected as <span className="text-green font-medium">{steamConnection.steam_username}</span>
                    </Typography>
                  ) : (
                    <Typography color="secondary" className="text-xs sm:text-sm">
                      Not connected
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Connect/Disconnect Button */}
              {steamConnection.connected ? (
                <Button
                  variant="secondary"
                  onClick={handleSteamDisconnect}
                  disabled={!steamConnection.can_disconnect}
                  className="text-sm"
                >
                  {steamConnection.can_disconnect ? 'Disconnect' : `${steamConnection.days_until_can_change}d`}
                </Button>
              ) : (
                <Button
                  isFancy
                  onClick={handleSteamConnect}
                  disabled={steamConnecting}
                  className="text-sm"
                >
                  {steamConnecting ? 'Connecting...' : 'Connect'}
                </Button>
              )}
            </Box>

            {/* Connected Info */}
            {steamConnection.connected && steamConnection.connected_at && (
              <Box className="bg-dark-gray/50 rounded-xl p-3 mb-4">
                <Typography color="secondary" className="text-xs">
                  Connected on {new Date(steamConnection.connected_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </Box>
            )}

            {/* Why Connect Info Box */}
            {!steamConnection.connected && (
              <Box className="bg-dark-gray/50 rounded-xl p-3 sm:p-4 border border-dark-border" col>
                <Box alignCenter className="gap-2 mb-2">
                  <Icon name="infoCircle" size={16} color="green" />
                  <Typography className="text-sm font-medium">Why connect Steam?</Typography>
                </Box>
                <Typography color="secondary" className="text-xs sm:text-sm leading-relaxed">
                  Connecting your Steam account links your in-game activity with your Cyberlete profile.
                  This is required for our anti-cheat system to verify your player account.
                </Typography>
              </Box>
            )}

            {/* Cooldown Warning */}
            {steamConnection.connected && !steamConnection.can_disconnect && (
              <Box className="bg-yellow-500/10 border border-yellow-500/30 rounded-xl p-3">
                <Typography className="text-xs sm:text-sm text-yellow-500">
                  You can only change your Steam account once every 30 days.
                  {steamConnection.days_until_can_change > 0 && (
                    <> You can change it in {steamConnection.days_until_can_change} days.</>
                  )}
                </Typography>
              </Box>
            )}
          </Box>

          {/* Discord Connection Section */}
          <Box className="border border-dark-border rounded-2xl bg-white/[0.02] p-4 sm:p-6 mb-4 sm:mb-6 transition-all duration-200 hover:border-green/20 hover:shadow-[0_0_24px_rgba(56,239,125,0.06)]" col>
            <Box alignCenter justify="between" className="mb-4 flex-wrap gap-3">
              <Box alignCenter className="gap-3 sm:gap-4">
                {/* Discord Logo */}
                <img
                  src="/assets/discord-icon.png"
                  alt="Discord"
                  className={`w-10 h-10 sm:w-12 sm:h-12 rounded-full flex-shrink-0 object-cover ring-2 ${
                    discordConnection.connected
                      ? 'ring-green/30 shadow-[0_0_12px_rgba(56,239,125,0.15)]'
                      : 'ring-dark-border'
                  }`}
                />
                <Box col className="min-w-0">
                  <Typography variant="h5" className="text-base sm:text-lg">Discord</Typography>
                  {discordConnection.connected ? (
                    <Typography color="secondary" className="text-xs sm:text-sm truncate">
                      Connected as <span className="text-green font-medium">{discordConnection.discord_username}</span>
                    </Typography>
                  ) : (
                    <Typography color="secondary" className="text-xs sm:text-sm">
                      Not connected
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Connect/Disconnect Button */}
              {discordConnection.connected ? (
                <Button
                  variant="secondary"
                  onClick={handleDiscordDisconnect}
                  className="text-sm"
                >
                  Disconnect
                </Button>
              ) : (
                <Button
                  isFancy
                  onClick={handleDiscordConnect}
                  disabled={discordConnecting}
                  className="text-sm"
                >
                  {discordConnecting ? 'Connecting...' : 'Connect'}
                </Button>
              )}
            </Box>

            {/* Connected Info */}
            {discordConnection.connected && discordConnection.connected_at && (
              <Box className="bg-dark-gray/50 rounded-xl p-3 mb-4">
                <Typography color="secondary" className="text-xs">
                  Connected on {new Date(discordConnection.connected_at).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric'
                  })}
                </Typography>
              </Box>
            )}

            {/* Why Connect Info Box */}
            {!discordConnection.connected && (
              <Box className="bg-dark-gray/50 rounded-xl p-3 sm:p-4 border border-dark-border" col>
                <Box alignCenter className="gap-2 mb-2">
                  <Icon name="infoCircle" size={16} color="green" />
                  <Typography className="text-sm font-medium">Why connect Discord?</Typography>
                </Box>
                <Typography color="secondary" className="text-xs sm:text-sm leading-relaxed">
                  Connecting your Discord account enables matchmaking through our Discord bot queue system
                  and links your Discord identity with your Cyberlete profile.
                </Typography>
              </Box>
            )}
          </Box>

          {/* Future Connections Placeholder */}
          <Box className="border border-dashed border-dark-border/60 rounded-2xl p-5 sm:p-6 flex items-center justify-center gap-3" col>
            <Box className="w-10 h-10 rounded-xl bg-gradient-to-br from-green/10 to-cyan/5 flex items-center justify-center">
              <Icon name="link" size={18} color="secondary" />
            </Box>
            <Typography color="secondary" className="text-xs sm:text-sm text-center">
              More connections coming soon (Twitch, etc.)
            </Typography>
          </Box>
        </>
      )}
    </Box>
  )

  return (
    <Modal isOpened={isOpen} onClose={onClose} title="Settings" withCard={false}>
      <Box className="bg-dark-card rounded-2xl overflow-hidden w-full" col>
        {/* Desktop: Segmented Pills | Mobile: Accordion */}
        {!isMobile ? (
          <>
            {/* Desktop Segmented Pills */}
            <Box className="px-6 pt-5 pb-2 flex justify-center">
              <div className="inline-flex rounded-full bg-dark-card/80 backdrop-blur border border-dark-border p-1">
                {tabItems.map((tab) => (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium transition-all duration-200 ${
                      activeTab === tab.id
                        ? 'bg-green/15 text-green shadow-[0_0_12px_rgba(56,239,125,0.15)]'
                        : 'text-gray-400 hover:text-white'
                    }`}
                  >
                    <Icon name={tab.icon as any} size={16} />
                    {tab.label}
                  </button>
                ))}
              </div>
            </Box>

            {/* Desktop Tab Content */}
            <Box className="p-6 max-h-[60vh] min-h-[400px] overflow-y-auto" col>
              {activeTab === 'notifications' && <NotificationsContent />}
              {activeTab === 'account' && <AccountContent />}
              {activeTab === 'privacy' && <PrivacyContent />}
              {activeTab === 'connections' && <ConnectionsContent />}

              {/* Desktop Action Buttons - only for notifications tab */}
              {activeTab === 'notifications' && !loading && (
                <Box className="flex gap-3 justify-end pt-5 border-t border-dark-border/50 flex-wrap">
                  <button
                    onClick={handleReset}
                    className="px-4 py-2 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/[0.05] transition-all duration-200"
                  >
                    Reset to Defaults
                  </button>
                  <button
                    onClick={onClose}
                    className="px-4 py-2 rounded-xl text-sm text-gray-400 hover:text-white hover:bg-white/[0.05] transition-all duration-200"
                  >
                    Cancel
                  </button>
                  <Button isFancy onClick={handleSave} disabled={saving} className="shadow-[0_0_16px_rgba(56,239,125,0.2)]">
                    {saving ? 'Saving...' : 'Save Settings'}
                  </Button>
                </Box>
              )}
            </Box>
          </>
        ) : (
          /* Mobile: Accordion Layout */
          <Box className="p-4 max-h-[70vh] overflow-y-auto" col>
            {/* Notifications Section */}
            <MobileSectionHeader
              id="notifications"
              title="Notifications"
              icon="bell"
              description="Email digests & alerts"
            />
            {expandedSections.has('notifications') && (
              <Box className="mb-4 px-2 animate-fadeIn" col>
                <NotificationsContent />
              </Box>
            )}

            {/* Account Section */}
            <MobileSectionHeader
              id="account"
              title="Account"
              icon="account"
              description="Profile & preferences"
            />
            {expandedSections.has('account') && (
              <Box className="mb-4 px-2 animate-fadeIn" col>
                <AccountContent />
              </Box>
            )}

            {/* Privacy Section */}
            <MobileSectionHeader
              id="privacy"
              title="Privacy"
              icon="lock"
              description="Data & visibility"
            />
            {expandedSections.has('privacy') && (
              <Box className="mb-4 px-2 animate-fadeIn" col>
                <PrivacyContent />
              </Box>
            )}

            {/* Connections Section */}
            <MobileSectionHeader
              id="connections"
              title="Connections"
              icon="link"
              description="Steam, Discord & more"
            />
            {expandedSections.has('connections') && (
              <Box className="mb-4 px-2 animate-fadeIn" col>
                <ConnectionsContent />
              </Box>
            )}

            {/* Mobile Action Buttons */}
            <Box className="flex flex-col gap-3 mt-4 pt-4 border-t border-dark-border/50">
              <Button isFancy onClick={handleSave} disabled={saving} stretch className="shadow-[0_0_16px_rgba(56,239,125,0.2)]">
                {saving ? 'Saving...' : 'Save Settings'}
              </Button>
              <Box className="flex gap-2">
                <button
                  onClick={handleReset}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-dark-border transition-all duration-200"
                >
                  Reset
                </button>
                <button
                  onClick={onClose}
                  className="flex-1 px-4 py-2.5 rounded-xl text-sm text-gray-400 hover:text-white bg-white/[0.03] hover:bg-white/[0.06] border border-dark-border transition-all duration-200"
                >
                  Cancel
                </button>
              </Box>
            </Box>
          </Box>
        )}
      </Box>
    </Modal>
  )
}

export default SettingsModal
